import { AdvisorProfileFileResponse, AdvisorProfileImageRequest } from "./advisorProfile.types";
import { DayOfWeek } from "./shared";

// Association portal (ASSOCIATION_USER): the association contract, verified
// against apoteka-back @ bd35a05f (AssociationController, AssociationService,
// AssociationRequest/Response) and OBSERVED on test.apoteka.rs on 2026-10-29
// (GET /association/mine and GET /association/{id} return the same shape).
// The logo split (`logo` beside `images`) follows apoteka-back @ cbd77705.

export interface AssociationCategoryResponse {
  id: number;
  name: string;
  ordinal: number;
  /** Only with `?withCounts=true`. */
  associationCount?: number;
}

/**
 * Association opening hours. Unlike the advisor schedule (`availableFrom` /
 * `availableUntil`), the association uses `openFrom` / `openUntil` in BOTH
 * directions, strictly "HH:mm". The backend does no ordering check.
 */
export interface AssociationWorkingHoursEntry {
  dayOfWeek: DayOfWeek;
  openFrom: string;
  openUntil: string;
}

/** Same `FileResponse` shape the advisor profile already types. */
export type AssociationFileResponse = AdvisorProfileFileResponse;

/**
 * Same media row algorithm as the advisor profile (kept by location, new rows
 * null), except that `isSmall` is meaningful here: `1` stores the row as the
 * association's logo, `0` as a banner.
 */
export type AssociationImageRequest = Omit<AdvisorProfileImageRequest, "isSmall"> & {
  isSmall: 0 | 1;
};

export interface AssociationResponse {
  id: number;
  name: string;
  description: string | null;
  address: string | null;
  city: string | null;
  postalCode: number | null;
  phoneNumber: string | null;
  latitude: number | null;
  longitude: number | null;
  /** Publication: an unpublished association stays in `/mine`, leaves the consumer list. */
  active: boolean;
  advisorCount: number | null;
  categories: AssociationCategoryResponse[];
  /** Banners and gallery only: the logo is never in here. */
  images: AssociationFileResponse[];
  /** The logo (stored with `isSmall: 1`), served on its own; null when there is none. */
  logo: AssociationFileResponse | null;
  workingHours: AssociationWorkingHoursEntry[];
  /** ISO local date-time, e.g. "2026-10-21T15:18:03". */
  created: string | null;
  isNew: boolean;
}

/**
 * The JSON part (`associationRequestString`) of `PUT /association/{id}`. The
 * part is parsed by hand on the server, so the bean annotations never run:
 * only `name` is validated there and the max lengths are enforced here.
 */
export interface AssociationRequest {
  /** Required, max 256. */
  name: string;
  description?: string | null;
  /** Max 256. */
  address?: string | null;
  /** Max 128. */
  city?: string | null;
  postalCode?: number | null;
  /** Max 64. */
  phoneNumber?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /** null = unchanged. */
  active?: boolean | null;
  /** null = unchanged, [] = clear. */
  categoryIds?: number[] | null;
  images?: AssociationImageRequest[];
  /** Default false = media untouched. True rebuilds the row set from `images`. */
  updateImages?: boolean;
  /** null = unchanged, [] = clear. Hours have their own PUT as well. */
  workingHours?: AssociationWorkingHoursEntry[] | null;
}

export const ASSOCIATION_NAME_MAX = 256;
export const ASSOCIATION_ADDRESS_MAX = 256;
export const ASSOCIATION_CITY_MAX = 128;
export const ASSOCIATION_PHONE_MAX = 64;
